import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar, Search, List } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";

export const NewsletterSearch = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState<'list' | 'grid'>('list');

  const { data: newsletters, isLoading } = useQuery({
    queryKey: ['newsletters', searchTerm],
    queryFn: async () => {
      let query = supabase
        .from('daily_newsletters')
        .select('*')
        .order('scheduled_date', { ascending: false });
      
      if (searchTerm) {
        query = query.or(`content.ilike.%${searchTerm}%,summary.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Newsletter Archive</CardTitle>
          <div className="flex gap-2">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search newsletters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 max-w-md"
          />
        </div>
        
        <div className="space-y-4">
          {isLoading ? (
            <div>Loading...</div>
          ) : newsletters?.map((newsletter) => (
            <div key={newsletter.id} className="border p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">{newsletter.summary}</h3>
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(newsletter.scheduled_date), 'MMM d, yyyy')}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                {newsletter.content.substring(0, 200)}...
              </div>
              <div className="mt-2">
                <Badge variant={newsletter.status === 'sent' ? 'secondary' : 'outline'}>
                  {newsletter.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};